import { useContext, useEffect, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import { mapModes } from '../../constants/map';
import { MapContext } from '../../contexts/MapContext';
import IconButton from '../basics/IconButton';
import ChevronIcon from '../icons/ChevronIcon';
import { SUITABILITY_LAYER_ID } from '../right-menu/SuitabilityMenu';
import FieldsMenuContent from '../right-menu/FieldsMenuContent';
import MobilePanel from './MobilePanel';
import RowsList from './RowsList';
import { CLIMATE_LAYER_ID } from '../right-menu/ClimateLayerMenu';
import { LAND_USE_LAYER_ID } from '../right-menu/LandUseLayerMenu';

const ToggleRowsButton = ({ showRows, setShowRows, text = 'Rows' }) => (
  <IconButton
    color="link"
    size="sm"
    className="font-tiny"
    onClick={() => setShowRows(!showRows)}
  >
    {text}
    <ChevronIcon direction={showRows ? 'up' : 'down'} />
  </IconButton>
);

const PanelTitle = ({ showRows, setShowRows }) => {
  const {
    mapState: {
      mapMode,
      activeField,
      activeRowIndexes,
      activeLayerId,
      activeLayer,
      activeLayerFeature,
      setMapMode,
    },
  } = useContext(MapContext);
  return (
    // Has active row
    mapMode === mapModes.ROW_ENGAGED ? (
      <div className="d-flex align-items-center">
        <Button
          color="link"
          className="p-0 mr-2 text-primary"
          size="sm"
          onClick={() => setMapMode(mapModes.SECTION_ENGAGED, activeField.id)}
        >
          {activeField.name}
        </Button>
        <ToggleRowsButton
          showRows={showRows}
          setShowRows={setShowRows}
          text={
            activeRowIndexes.length === 1
              ? `Row ${activeRowIndexes[0] + 1}`
              : `Rows`
          }
        />
      </div>
    ) : // Has active field
    activeField ? (
      <span className="d-flex align-items-center">
        {activeField.name}
        <ToggleRowsButton showRows={showRows} setShowRows={setShowRows} />
      </span>
    ) : // Has active layer feature
    activeLayerFeature ? (
      <>
        <Button
          color="link"
          onClick={setMapMode(mapModes.LAYER_ENGAGED, activeLayerId)}
        >
          {activeLayer.displayName}
        </Button>{' '}
        {activeLayerFeature.properties?.name || 'Feature'}
      </>
    ) : // Suitability layer
    activeLayerId === SUITABILITY_LAYER_ID ? (
      'Suitability'
    ) : activeLayerId === LAND_USE_LAYER_ID ? (
      'Historical Land Use'
    ) : activeLayerId === CLIMATE_LAYER_ID ? (
      'Climate'
    ) : // Has active layer
    activeLayer ? (
      activeLayer.displayName
    ) : (
      // None of the above.
      'Project Summary'
    )
  );
};

const MobileFieldsPanel = ({ colors, onClose, bottom }) => {
  const {
    mapState: {
      mapMode,
      activeField,
      activeRowIndexes,
      activeLayerId,
      activeLayerFeatureId,
      setMapMode,
    },
  } = useContext(MapContext);

  const [showRows, setShowRows] = useState(false);
  // Ref to the scrolling container element in the MobilePanel
  const containerRef = useRef();

  useEffect(() => {
    setShowRows(false);
  }, [mapMode, activeField, activeRowIndexes]);

  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.scrollTop = 0;
  }, [
    showRows,
    activeRowIndexes,
    activeField,
    activeLayerFeatureId,
    activeLayerId,
  ]);

  return (
    <MobilePanel
      onClose={() => {
        setMapMode(mapModes.VIEW);
        onClose();
      }}
      title={<PanelTitle showRows={showRows} setShowRows={setShowRows} />}
      scrollingContainerRef={containerRef}
      bottom={bottom}
    >
      {activeField && showRows && (
        <RowsList
          rows={activeField.rows?.features}
          containerRef={containerRef}
          sectionId={activeField.id}
        />
      )}
      <FieldsMenuContent readonly colors={colors} />
    </MobilePanel>
  );
};

export default MobileFieldsPanel;
